import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react'

import { SearchContext } from './search/SearchContext'
import { useSelectSearch } from './search/useSelectSearch'
import { SelectionContext } from './selection/SelectionContext'
import { useSelectAll } from './selection/useSelectAll'

import { SelectStateContext } from './SelectStateContext'
import type { SearchReactElement, SelectOption, SelectProps } from './types'

type SelectProviderProps = {
  options: SelectProps['options']
  value: SelectProps['value']
  onChange?: SelectProps['onChange']
  mode: SelectProps['mode']
  size: SelectProps['size']
  placeholder: string
  error: boolean
  disabled: boolean
  readonly: boolean
  ariaLabel?: string
  maxTagsDisplayed?: number
  searchComponent?: SearchReactElement
}

export const SelectProvider: FC<PropsWithChildren<SelectProviderProps>> = ({
  options: rawOptions,
  value,
  onChange,
  mode,
  size,
  placeholder,
  error,
  disabled,
  readonly,
  ariaLabel,
  maxTagsDisplayed,
  searchComponent,
  children,
}) => {
  // State context
  const [isOpen, setIsOpen] = useState(false)

  // For group mode, we improve display value by adding parent label to children options (e.g.: "Option 1 (Group A)")
  const options: SelectOption[] = useMemo(() => {
    if (mode !== 'group') return rawOptions

    return rawOptions.map(option => {
      if (!option.children) return option

      return {
        ...option,
        type: 'group',
        children: option.children.map(child => ({
          ...child,
          type: 'option',
          displayLabel: `${child.label} (${option.label})`,
        })),
      }
    })
  }, [mode, rawOptions])

  // Search context
  const { searchValue, handleSearchChange, resetSearch, filteredOptions, hasSearch, loading } =
    useSelectSearch({ options, searchComponent })

  // Aplatir les options pour inclure les enfants (mode group)
  const flattenedOptions = useMemo(
    () => options.flatMap(option => [option, ...(option.children ?? [])]),
    [options],
  )

  // Selection context
  const selectedValues = useMemo(() => {
    if (mode === 'single') {
      return value ? [value as string] : []
    }
    return Array.isArray(value) ? (value as string[]) : []
  }, [mode, value])

  // Remember labels for selected values across option refetches (e.g. async-loaded
  // filter options that exclude a previously-selected value). Lifetime = this
  // Select's mount; cleared on unmount/refresh.
  const [labelCache] = useState(() => new Map<string, SelectOption>())

  const selectedOptions = useMemo(() => {
    const currentByValue = new Map(flattenedOptions.map(opt => [opt.value, opt]))

    for (const value of selectedValues) {
      const match = currentByValue.get(value)
      if (match) labelCache.set(value, match)
    }

    return selectedValues
      .map(value => currentByValue.get(value) ?? labelCache.get(value))
      .filter((opt): opt is SelectOption => opt !== undefined)
  }, [flattenedOptions, selectedValues, labelCache])

  const flattenFiltered = useCallback((opts: SelectOption[]): SelectOption[] => {
    return opts.reduce((acc, option) => {
      if (option.children && option.children.length > 0) {
        // For groups, add only non-disabled children
        acc.push(...option.children.filter(child => !option.disabled && !child.disabled))
      } else if (!option.disabled) {
        acc.push(option)
      }
      return acc
    }, [] as SelectOption[])
  }, [])

  const selectableOptions = useMemo(
    () => flattenFiltered(filteredOptions),
    [filteredOptions, flattenFiltered],
  )

  const { handleSelectAll, selectAllState } = useSelectAll<SelectOption>({
    selectedOptions,
    selectableOptions,
    onSelectionChange: options => onChange?.(options.map(o => o.value) as never),
    getOptionKey: option => option.value,
  })
  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (!open) resetSearch()
    },
    [resetSearch],
  )
  const handleSelect = useCallback(
    (optionValue: string) => {
      if (mode === 'single') {
        onChange?.(optionValue as never)
        setIsOpen(false)
        return
      }
      const newValues = selectedValues.includes(optionValue)
        ? selectedValues.filter(v => v !== optionValue)
        : [...selectedValues, optionValue]
      onChange?.(newValues as never)
    },
    [mode, onChange, selectedValues],
  )

  const clearSelection = useCallback(() => {
    if (mode === 'single') {
      onChange?.('' as never)
      return
    }
    onChange?.([] as never)
  }, [mode, onChange])

  const handleGroupSelect = useCallback(
    (group: SelectOption) => {
      if (!group.children || mode === 'single') return

      const childValues = group.children.map(child => child.value)
      const selectedChildren = childValues.filter(v => selectedValues.includes(v))
      const allSelected = selectedChildren.length === childValues.length

      if (allSelected) {
        // Deselect all children
        const newValues = selectedValues.filter(v => !childValues.includes(v))
        onChange?.(newValues as never)
      } else {
        // Select all children (that are not disabled)
        const selectableChildren = group.children
          .filter(child => !child.disabled)
          .map(child => child.value)
        const newValues = [...new Set([...selectedValues, ...selectableChildren])]
        onChange?.(newValues as never)
      }
    },
    [mode, onChange, selectedValues],
  )

  const selectStateContextValue = useMemo(
    () => ({
      size: size ?? 'md',
      error,
      disabled,
      readonly,
      ariaLabel,
      isOpen,
      setIsOpen: handleOpenChange,
      maxTagsDisplayed,
      placeholder,
      mode: mode ?? 'single',
      value,
      onChange: onChange as ((value: string | string[]) => void) | undefined,
      options,
      flattenedOptions,
    }),
    [
      size,
      error,
      disabled,
      readonly,
      ariaLabel,
      isOpen,
      handleOpenChange,
      maxTagsDisplayed,
      placeholder,
      mode,
      value,
      onChange,
      options,
      flattenedOptions,
    ],
  )

  const searchContextValue = useMemo(
    () => ({
      hasSearch,
      searchValue,
      handleSearchChange,
      filteredOptions,
      searchComponent,
      loading,
    }),
    [hasSearch, searchValue, handleSearchChange, filteredOptions, searchComponent, loading],
  )

  const selectionContextValue = useMemo(
    () => ({
      selectedValues,
      selectedOptions,
      handleSelect,
      handleSelectAll,
      handleGroupSelect,
      selectAllState,
      clearSelection,
    }),
    [
      selectedValues,
      selectedOptions,
      handleSelect,
      handleSelectAll,
      handleGroupSelect,
      selectAllState,
      clearSelection,
    ],
  )

  // Provide contexts
  return (
    <SelectStateContext value={selectStateContextValue}>
      <SearchContext value={searchContextValue}>
        <SelectionContext value={selectionContextValue}>{children}</SelectionContext>
      </SearchContext>
    </SelectStateContext>
  )
}
